import { CompanyModalV2Content } from "./CompanyModalV2Content";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Drawer, SwipeableDrawer } from "@mui/material";

import { CompanyPropTypes } from "../../schema/company";
import useIsMobile from "../../hooks/useIsMobile";
import { MOBILE_SHEET_EXPANDED_VH } from "../../theme/mobileLayout";

import useCompanyEnrichmentHistory from "./v2/useCompanyEnrichmentHistory";

/**
 * Desktop / expanded mobile company identity + tabs.
 * Desktop keeps full header (links, description, chips). Mobile expanded uses compact header only.
 */

export default function CompanyModalV2({
  open,
  setOpen,
  companyDetail,
  imageData,
  draftSessionId,
  onDraftSessionUsed,
  onRunEnrichmentAction,
}) {
  const isMobile = useIsMobile();
  const actionsRef = useRef(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const handleClose = () => setOpen(false);
  const {
    enrichmentHistory,
    enrichmentHistoryError,
    enrichmentHistoryLoading,
    handleActionResult,
    handleActionStart,
  } = useCompanyEnrichmentHistory({ companyDetail, open, setActiveTab });

  useEffect(() => {
    if (!open) {
      setDescriptionExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    setDescriptionExpanded(false);
    setActiveTab(0);
  }, [companyDetail?.stagedId, companyDetail?.alphasearchId, companyDetail?.primaryName]);

  if (!open) return null;

  if (isMobile) {
    return (
      <SwipeableDrawer
        open={open}
        onClose={handleClose}
        onOpen={() => {}}
        anchor="bottom"
        disableSwipeToOpen
        swipeAreaWidth={0}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          "data-testid": "company-modal-v2",
          "data-mobile-sheet": "true",
          "data-sheet-state": "expanded",
          sx: {
            height: `${MOBILE_SHEET_EXPANDED_VH}vh`,
            maxHeight: `${MOBILE_SHEET_EXPANDED_VH}vh`,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            pt: 1,
            pb: 0.5,
            display: "flex",
            justifyContent: "center",
            minHeight: 28,
          }}
          aria-hidden
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(88, 89, 91, 0.28)",
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <CompanyModalV2Content
            companyDetail={companyDetail}
            imageData={imageData}
            onClose={handleClose}
            compact
            stickyHeader
            activeTab={activeTab}
            onTabChange={setActiveTab}
            enrichmentHistory={enrichmentHistory}
            enrichmentHistoryError={enrichmentHistoryError}
            enrichmentHistoryLoading={enrichmentHistoryLoading}
            onActionStart={handleActionStart}
            onActionResult={handleActionResult}
            actionsRef={actionsRef}
            draftSessionId={draftSessionId}
            onDraftSessionUsed={onDraftSessionUsed}
            onRunEnrichmentAction={onRunEnrichmentAction}
          />
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      sx={{ scrollbarGutter: "stable both-edges" }}
      PaperProps={{
        "data-testid": "company-modal-v2",
        sx: {
          width: { xs: "100%", sm: "min(60vw, 960px)" },
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CompanyModalV2Content
        companyDetail={companyDetail}
        imageData={imageData}
        onClose={handleClose}
        compact={false}
        descriptionExpanded={descriptionExpanded}
        onToggleDescription={() => setDescriptionExpanded((value) => !value)}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        enrichmentHistory={enrichmentHistory}
        enrichmentHistoryError={enrichmentHistoryError}
        enrichmentHistoryLoading={enrichmentHistoryLoading}
        onActionStart={handleActionStart}
        onActionResult={handleActionResult}
        actionsRef={actionsRef}
        draftSessionId={draftSessionId}
        onDraftSessionUsed={onDraftSessionUsed}
        onRunEnrichmentAction={onRunEnrichmentAction}
      />
    </Drawer>
  );
}

CompanyModalV2.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  companyDetail: CompanyPropTypes,
  imageData: PropTypes.string,
  draftSessionId: PropTypes.string,
  onDraftSessionUsed: PropTypes.func,
  onRunEnrichmentAction: PropTypes.func,
};

CompanyModalV2Content.propTypes = {
  companyDetail: CompanyPropTypes,
  imageData: PropTypes.string,
  onClose: PropTypes.func,
  compact: PropTypes.bool,
  descriptionExpanded: PropTypes.bool,
  onToggleDescription: PropTypes.func,
  stickyHeader: PropTypes.bool,
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  enrichmentHistory: PropTypes.arrayOf(PropTypes.object),
  enrichmentHistoryError: PropTypes.string,
  enrichmentHistoryLoading: PropTypes.bool,
  onActionStart: PropTypes.func,
  onActionResult: PropTypes.func,
  actionsRef: PropTypes.shape({ current: PropTypes.any }),
  draftSessionId: PropTypes.string,
  onDraftSessionUsed: PropTypes.func,
  onRunEnrichmentAction: PropTypes.func,
};
